import * as React from "react";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, Link as ReactRouterLink } from "react-router-dom";
import {
  SurveySelectableAppMeta,
  DownloadCsvModal,
} from "@graffer-inc/smart-apply";
import {
  Pagination,
  SecondaryButton,
  DangerButton,
  ExternalLink,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  Th,
  ErrorDialog,
  WarningBanner,
  DateRangeInput,
} from "@graffer-inc/console-design-kit";
import { RBACHidden } from "../../../../helpers/rbac/rbac";
import { parseQueryOfLocation } from "../../../../../utils/url-utils";
import { useListKeys, useModal } from "../../../../../utils/custom-hooks";
import {
  SurveyAnswerTableHeader,
  SurveyAnswerTableBody,
} from "../../../../../application/service/surveys/generator/survey-answer-table-generator";
import { LoadingModal } from "../../../../organisms/smart-apply/modals/loading-modal";
import { CSVEncoding } from "../../../../../application/domain/file-encoding";

type ResultsProps = {
  dateRange: {
    from?: Date;
    to?: Date;
  };
  setDateRange: (dateRange: { from?: Date; to?: Date }) => void;
  tableData: {
    header: SurveyAnswerTableHeader;
    body: SurveyAnswerTableBody[];
  };
  onCSVDownload: () => void;
  canDownloadSurveyCsv: boolean;
  fetchResults: () => void;
  paging: {
    current: number;
    total: number;
  };
  setPage: (p: number) => void;
  movePage: (p: number) => void;
  selectable: SurveySelectableAppMeta;
  requireSelectableRawValue: boolean;
  onChangeRequireSelectableRawValue: (isRequired: boolean) => void;
  formatFileResourceURL: (answerId: string, refKey: string) => string;
  onFileBulkDownload: () => void;
  canDeleteAnswers: boolean;
  onDelete: () => void;
  csvEncoding: CSVEncoding;
  onChangeCSVEncoding: (encoding: CSVEncoding) => void;
  resetCSVModalState: () => void;
};

export const Results: React.FC<ResultsProps> = ({
  dateRange,
  onCSVDownload,
  setDateRange,
  tableData,
  canDownloadSurveyCsv,
  fetchResults,
  paging,
  setPage,
  movePage,
  selectable,
  requireSelectableRawValue,
  onChangeRequireSelectableRawValue,
  formatFileResourceURL,
  onFileBulkDownload,
  canDeleteAnswers,
  onDelete,
  csvEncoding,
  onChangeCSVEncoding,
  resetCSVModalState,
}) => {
  const { page } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
  };
  const downloadCsvModal = useModal();
  const deleteConfirmModal = useModal();

  const hasResults = useMemo(() => {
    return tableData.body.length > 0;
  }, [tableData.body]);

  const firstRowHeaderKeys = useListKeys(tableData.header.fisrtRowHeaders);
  const secondRowHeaderKeys = useListKeys(tableData.header.secondRowHeaders);
  const columnKeys = useListKeys(hasResults ? tableData.body[0].answers : []);
  const rowKeys = useListKeys(tableData.body);

  useEffect(() => {
    setPage(parseInt(page || "1", 10));
    fetchResults();
  }, [page, setPage, fetchResults]);

  const setFilterValue = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: Date | null;
      endDate: Date | null;
    }) => {
      setDateRange({
        from: startDate ?? undefined,
        to: endDate ?? undefined,
      });
    },
    [setDateRange],
  );

  const haveAttachedFile = (body: SurveyAnswerTableBody[]): boolean =>
    body.some((b) => b.answers.some((a) => a.file));

  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  return (
    <>
      <div className="columns">
        <div className="column is-narrow">
          <DateRangeInput
            startDate={dateRange.from}
            endDate={dateRange.to}
            setValue={setFilterValue}
          />
        </div>
        <div className="column is-narrow">
          {canDownloadSurveyCsv && (
            <SecondaryButton
              text={"CSVダウンロード"}
              onClick={downloadCsvModal.open}
              icon="PaperIcon"
            />
          )}
        </div>
        {haveAttachedFile(tableData.body) && (
          <div className="column is-narrow">
            <SecondaryButton
              text={`${paging.current}ページ目のファイルをダウンロード`}
              onClick={async () => {
                setIsBulkDownloading(true);
                await onFileBulkDownload();
                setIsBulkDownloading(false);
              }}
              icon="DownloadIcon"
            />
          </div>
        )}
        {canDeleteAnswers && (
          <RBACHidden requirePermissions="survey_answers.delete">
            <div className="column is-narrow">
              <DangerButton
                text={`アンケートデータを全て削除`}
                onClick={deleteConfirmModal.open}
                icon="TrashIcon"
              />
            </div>
          </RBACHidden>
        )}
      </div>
      <hr />
      {/* 質問項目が多いと、回答表示のテーブルが横に伸びてしまい、回答が最後まで閲覧できなくなってしまうため、横幅を固定 */}
      {/* ref. https://github.com/graffer-inc/cloud-gov/pull/12425 */}
      {/* TODO: テーブルの親要素が、テーブルの拡大に合わせて広がらないようにする */}
      <div className="columns" style={{ width: "80vw" }}>
        <div className="column is-full">
          <Table borderType={"all"}>
            <TableHeader>
              <TableRow>
                {tableData.header.fisrtRowHeaders.map((h, i) => (
                  <Th
                    key={firstRowHeaderKeys[i]}
                    colSpan={h.colSpan}
                    rowSpan={h.rowSpan}
                  >
                    <abbr title="Position">{h.label}</abbr>
                  </Th>
                ))}
              </TableRow>
              {tableData.header.secondRowHeaders.length > 0 && (
                <TableRow>
                  {tableData.header.secondRowHeaders.map((h, i) => (
                    <Th key={secondRowHeaderKeys[i]}>
                      <abbr title="Position">{h.label}</abbr>
                    </Th>
                  ))}
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              {tableData.body.map((row, i) => (
                <TableRow key={rowKeys[i]}>
                  {row.answers.map((cell, j) => (
                    <Td key={columnKeys[j]}>
                      {cell.type === "file" && !!cell.value ? (
                        <ExternalLink
                          icon="DownloadIcon"
                          anchor={({ ...props }) => (
                            <ReactRouterLink
                              {...props}
                              to={formatFileResourceURL(
                                row.answerId,
                                cell.value,
                              )}
                            />
                          )}
                        >
                          {cell.file?.name || ""}
                        </ExternalLink>
                      ) : (
                        cell.value
                      )}
                    </Td>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div style={{ marginTop: "16px" }}>
            <Pagination
              onPageClick={movePage}
              currentPage={paging.current}
              lastPage={paging.total}
            />
          </div>
        </div>
      </div>
      <DownloadCsvModal
        isActive={downloadCsvModal.isActive}
        selectable={selectable}
        requireSelectableRawValue={requireSelectableRawValue}
        onChangeRequireSelectableRawValue={onChangeRequireSelectableRawValue}
        onCSVDownload={onCSVDownload}
        onClose={downloadCsvModal.close}
        csvEncoding={csvEncoding}
        onChangeCSVEncoding={onChangeCSVEncoding}
        resetCSVModalState={resetCSVModalState}
      />
      {canDeleteAnswers && deleteConfirmModal.isActive && (
        <ErrorDialog
          title="アンケートデータの削除"
          proceedText="削除する"
          onProceed={async () => {
            await onDelete();
            deleteConfirmModal.close();
          }}
          cancelText="キャンセル"
          onCancel={deleteConfirmModal.close}
          contents={
            <div className="field is-horizontal">
              <div className="field-body">
                <div className="field">
                  <label className="label m-2">
                    アンケートデータを全て削除します。よろしいですか？
                  </label>
                  <WarningBanner
                    message={"削除したアンケートデータは復元できません。"}
                    description={
                      "事前にダウンロードを済ませてから実施してください。"
                    }
                  />
                </div>
              </div>
            </div>
          }
        />
      )}
      {isBulkDownloading && (
        <LoadingModal
          isActive
          title="添付ファイル一括ダウンロード"
          body="添付ファイルをダウンロードしています。"
        />
      )}
    </>
  );
};
